import { APICONFIG } from '@config/api.config';
import { uploadCompressFile } from '@helper/fileUpload';
import { APIService } from '@services/api/common-services';
import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import { PresignedURLDetails } from '../enduser-details';

interface UploadFreshSiteImagesInput {
	files: File[];
	folder?: string;
}

interface UploadFreshSiteImages {
	mutate: UseMutateFunction<PresignedURLDetails[], Error, UploadFreshSiteImagesInput, unknown>;
	error: Error | null;
	isError: boolean;
	isPending: boolean;
}

interface PresignedFileRequest {
	folder: string;
	filename: string;
}

async function requestPresignedUrls(files: File[], folder: string): Promise<PresignedURLDetails[]> {
	const payload: PresignedFileRequest[] = files.map((file) => ({
		folder,
		filename: file.name,
	}));
	const urls = await APIService.postData<PresignedURLDetails[]>(APICONFIG.PRESIGNED_URL, payload);

	if (!urls || urls.length < files.length || urls.some((url) => !url?.pre_signed_url)) {
		throw new Error('Pre-signed URL missing for visit image.');
	}

	return urls;
}

async function uploadFiles(files: File[], urls: PresignedURLDetails[]): Promise<void> {
	await Promise.all(files.map((file, index) => uploadCompressFile(file, urls[index].pre_signed_url)));
}

export async function uploadFreshSiteImages(files: File[], folder = 'sitelead'): Promise<PresignedURLDetails[]> {
	if (!files.length) {
		return [];
	}

	const urls = await requestPresignedUrls(files, folder);
	await uploadFiles(files, urls);
	return urls;
}

export const useUploadFreshSiteImages = (): UploadFreshSiteImages => {
	const { mutate, error, isError, isPending } = useMutation({
		mutationFn: ({ files, folder }: UploadFreshSiteImagesInput) => uploadFreshSiteImages(files, folder),
	});

	return {
		mutate,
		error,
		isError,
		isPending,
	};
};
